import { useState } from 'react';
import * as yup from 'yup';
import { PASSWORD_RULES_REGEX } from '../constants/passwordValidation';

// Estado em que todos os requisitos foram atendidos (nenhum erro).
const noErrorsState = {
  lowercase: false,
  number: false,
  minAmount: false,
  specialCharacter: false,
  uppercase: false,
  required: false,
};

// Estado inicial: nada digitado ainda, todos os requisitos pendentes.
const allPendingState = {
  lowercase: true,
  number: true,
  minAmount: true,
  specialCharacter: true,
  uppercase: true,
  required: true,
};

/**
 * Controla a checklist de requisitos da nova senha (minúscula, maiúscula,
 * número, caractere especial e tamanho mínimo) em tempo real.
 *
 * Retorna `validatePassword` para plugar no `validate` do Field do Formik:
 * cada requisito não atendido vira `true` em `customErrors`, alimentando os
 * checkboxes de feedback.
 */
export default function usePasswordValidation() {
  const [customErrors, setCustomErrors] = useState(allPendingState);

  // Há requisito de regra falhando (ignorando o "required", tratado à parte).
  const verifyCheckErrors = () =>
    !customErrors.required &&
    Object.keys(customErrors)
      .filter(key => key !== 'required')
      .some(key => customErrors[key]);

  const validatePassword = async value => {
    const schema = yup
      .string()
      .matches(PASSWORD_RULES_REGEX.lowercase, {
        excludeEmptyString: true,
        message: 'lowercase',
      })
      .matches(PASSWORD_RULES_REGEX.uppercase, {
        excludeEmptyString: true,
        message: 'uppercase',
      })
      .matches(PASSWORD_RULES_REGEX.number, {
        excludeEmptyString: true,
        message: 'number',
      })
      .matches(PASSWORD_RULES_REGEX.minAmount, {
        excludeEmptyString: true,
        message: 'minAmount',
      })
      .matches(PASSWORD_RULES_REGEX.specialCharacter, {
        excludeEmptyString: true,
        message: 'specialCharacter',
      })
      .required('required')
      .nullable();

    try {
      await schema.validate(value, { abortEarly: false });
      setCustomErrors(noErrorsState);
      return undefined;
    } catch (e) {
      if (e && e.errors.length === 1 && e.errors[0] === 'required')
        e.errors.push(...Object.keys(noErrorsState));
      const parsedErrors = e.errors.reduce(
        (acc, curr) => ({ ...acc, [curr]: true }),
        noErrorsState,
      );
      setCustomErrors(parsedErrors);
      return parsedErrors;
    }
  };

  return { customErrors, validatePassword, verifyCheckErrors };
}
